import { useEffect, useMemo, useRef, useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { SearchMd, XClose } from "@untitledui/icons"

import {
  ALL_SECTIONS,
  COMPONENT_OPTIONS,
  SIDE_OPTIONS,
  type SideBucket,
  sectionLabel,
} from "@/lib/estimates/catalog"
import { lineKey } from "@/lib/estimates/photo-links"
import type { CanvasLine, ComponentPick } from "./use-canvas"

// The parts list, searched. From a photo the side is the photo's; from the
// estimate you say which side. A part already on the estimate for that
// side says so — picking it lists it, it never duplicates.

export function ComponentPicker({
  open,
  title,
  side: initialSide,
  askSide,
  lines,
  onPick,
  onClose,
}: {
  open: boolean
  title: string
  side: SideBucket
  askSide: boolean
  lines: CanvasLine[]
  onPick: (pick: ComponentPick) => void
  onClose: () => void
}) {
  const reduce = useReducedMotion()
  const [q, setQ] = useState("")
  const [side, setSide] = useState<SideBucket>(initialSide)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return
    setQ("")
    setSide(initialSide)
    const t = window.setTimeout(() => inputRef.current?.focus(), 30)
    return () => window.clearTimeout(t)
  }, [open, initialSide])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, onClose])

  const existing = useMemo(() => new Set(lines.map((l) => lineKey(l.component, l.side))), [lines])

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const hits = COMPONENT_OPTIONS.filter(
      (o) => !needle || o.label.toLowerCase().includes(needle) || sectionLabel(o.section).toLowerCase().includes(needle),
    )
    return ALL_SECTIONS.map((s) => ({ section: s, items: hits.filter((o) => o.section === s) })).filter(
      (g) => g.items.length > 0,
    )
  }, [q])

  const pick = (component: string) => onPick({ component, side })

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end justify-center bg-zinc-900/30 p-0 sm:items-center sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={onClose}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            initial={reduce ? { opacity: 0 } : { opacity: 0, transform: "translateY(12px) scale(0.98)" }}
            animate={{ opacity: 1, transform: "translateY(0px) scale(1)" }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, transform: "translateY(8px) scale(0.98)" }}
            transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="flex max-h-[88dvh] w-full flex-col rounded-t-2xl bg-white shadow-xl ring-1 ring-zinc-900/10 sm:max-w-[520px] sm:rounded-2xl"
          >
            <div className="flex items-center justify-between gap-3 px-4 pb-2 pt-4">
              <h2 className="text-[15px] font-semibold text-zinc-900">{title}</h2>
              <button
                type="button"
                onClick={onClose}
                className="grid size-9 place-items-center rounded-full text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/30"
                aria-label="Close"
              >
                <XClose className="size-4" />
              </button>
            </div>

            <div className="flex gap-2 px-4 pb-3">
              <div className="relative min-w-0 flex-1">
                <SearchMd className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-zinc-400" aria-hidden />
                <input
                  ref={inputRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && groups[0]?.items[0]) pick(groups[0].items[0].value)
                  }}
                  placeholder="Search parts"
                  aria-label="Search parts"
                  className="h-11 w-full rounded-lg border border-zinc-300 bg-white pl-9 pr-3 text-[14px] text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
                />
              </div>
              {askSide && (
                <select
                  value={side}
                  onChange={(e) => setSide(e.target.value as SideBucket)}
                  aria-label="Side"
                  className="h-11 shrink-0 rounded-lg border border-zinc-300 bg-white px-2.5 text-[14px] text-zinc-900 focus:border-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
                >
                  {SIDE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              )}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto border-t border-zinc-100 px-2 pb-2">
              {groups.length === 0 ? (
                <p className="px-3 py-8 text-center text-[13px] text-zinc-500">No part matches “{q}”.</p>
              ) : (
                groups.map((g) => (
                  <div key={g.section} className="pt-2">
                    <p className="px-3 pb-1 pt-1 text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-500">
                      {sectionLabel(g.section)}
                    </p>
                    <ul>
                      {g.items.map((o) => {
                        const on = existing.has(lineKey(o.value, side))
                        return (
                          <li key={o.value}>
                            <button
                              type="button"
                              onClick={() => pick(o.value)}
                              className="flex h-11 w-full items-center justify-between gap-3 rounded-lg px-3 text-left text-[14px] text-zinc-900 hover:bg-zinc-50 focus-visible:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/20"
                            >
                              <span>{o.label}</span>
                              {on && <span className="text-[12px] text-zinc-500">on the estimate</span>}
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
